import { cn } from "@/lib/utils";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@altimateai/lego";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useMutation } from '@tanstack/react-query';
import { z } from "zod";
import { Loader } from "../../components/loader";
import { postForgotPassword } from "../../helpers/apis";
import { BaseContainer, Header } from "./components/base";
import { FormInput, FormSubmit } from "./components/form";

const forgotPasswordSchema = z.object({
  email: z.string().min(1, "Please Enter Your Email").email("Email should be valid"),
});

type ForgotFormValues = z.infer<typeof forgotPasswordSchema>;

export function Forgot() {
  const { mutate, isSuccess, data, isPending: isLoading, error } = useMutation({
    mutationFn: postForgotPassword,
  });

  const form = useForm<ForgotFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
    mode: "onBlur",
  });

  const apiError =
    typeof error === "string" ? error : error instanceof Error ? error.message : null;
  const isOK = isSuccess && data?.ok;

  // TODO: move this to a util as it is called in multiple places
  const isMobile = window.innerWidth <= 768;

  const hasError = apiError || form.formState.errors.email;

  return (
    <>
      {isLoading && <Loader />}
      <BaseContainer footer={<></>}>
        <div className={cn({ "al-w-[90vw]": isMobile })}>
          <Header>Forgot password?</Header>
          <Form {...form}>
            <form
              noValidate
              onSubmit={form.handleSubmit(values => {
                mutate(values);
              })}
              className={isMobile ? "al-w-full al-mt-3" : "al-mt-4"}
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="al-space-y-[1px]">
                    <FormControl>
                      <FormInput
                        type="email"
                        placeholder="Enter email"
                        insetRing
                        className="al-py-6"
                        isInvalid={!!hasError}
                        {...field}
                      />
                    </FormControl>
                    {hasError && (
                      <div className="al-rounded-b-xl al-bg-bg-red al-text-action-red al-text-sm al-p-2">
                        {apiError ? (
                          <div role="alert">
                            <span>{apiError}</span>
                          </div>
                        ) : (
                          <FormMessage />
                        )}
                      </div>
                    )}
                  </FormItem>
                )}
              />

              <div className="al-text-center al-mt-4">
                <FormSubmit disabled={isOK}>Send instructions</FormSubmit>
              </div>
            </form>
          </Form>
          {isOK && (
            <div className="al-mt-3 al-text-primary">
              Password reset instructions are sent if it&apos;s a registered email
            </div>
          )}
        </div>
      </BaseContainer>
    </>
  );
}
